import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, register, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/dashboard';

  React.useEffect(() => {
    if (user) navigate(nextPath, { replace: true });
  }, [user, navigate, nextPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success('Account created successfully');
      } else {
        await login(email, password);
        toast.success('Welcome back');
      }
      navigate(nextPath, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1F3F] relative overflow-hidden flex-col justify-between p-12">
        <div className="relative z-10">
          <Link to="/">
            <img
              src="/10_VARfacts_logo_header_400x100.png"
              alt="VARfacts"
              className="h-10 opacity-90"
            />
          </Link>
        </div>
        <div className="relative z-10 space-y-6">
          <h2 className="font-heading text-3xl text-white leading-tight">
            Digital Asset<br />Documentation Triage
          </h2>
          <div className="w-16 h-0.5 bg-[#C5A55A]" />
          <p className="text-white/60 text-sm max-w-sm leading-relaxed">
            Clean, file-ready documentation for digital asset productions. 
            Structured, source-cited packets for legal review and discovery.
          </p>
        </div>
        <p className="relative z-10 text-white/30 text-xs">
          &copy; 2026 VARfacts. All Rights Reserved.
        </p>
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A55A]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C5A55A]/3 rounded-full translate-y-1/3 -translate-x-1/3" />
      </div>

      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F5F3EF]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/">
              <img src="/02_VARfacts_logo_navy_transparent.png" alt="VARfacts" className="h-8" />
            </Link>
          </div>

          <div className="space-y-2 mb-8">
            <h1 className="font-heading text-3xl font-semibold text-[#0B1F3F]" data-testid="login-title">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-sm text-[#64748B]">
              {isRegister
                ? 'Create an account to begin a matter and continue to secure intake'
                : 'Sign in to access your matters and secure upload path'}
            </p>
          </div>

          {/* Google OAuth */}
          <Button
            data-testid="google-login-btn"
            onClick={() => googleLogin(nextPath)}
            variant="outline"
            className="w-full h-12 bg-white border-[#D4D4D0] hover:bg-gray-50 text-[#0B1F3F] font-medium gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <Separator className="bg-[#D4D4D0]" />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#F5F3EF] px-3 text-xs text-[#64748B] uppercase tracking-wide">
              or
            </span>
          </div>

          {/* Email/Password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2 animate-fade-in-up">
                <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                  <Input
                    data-testid="register-name-input"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="pl-10 h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full h-12 btn-primary-varfacts rounded-sm"
            >
              {submitting ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#0B1F3F] font-semibold hover:underline"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
